import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { useWorkspaces, type WorkspaceRead, type WorkspaceRole } from './queries'

/**
 * Current-workspace state shared across the authenticated app.
 *
 * Why a context (not a query/atom):
 *  - The selection is *client state* layered on top of the cached workspaces
 *    list. TanStack Query already owns the list; this provider only owns
 *    "which one is active right now".
 *  - Multiple surfaces need to read AND switch the selection (sidebar
 *    switcher, workspaces page, route guards on later slices). A context
 *    keeps the wiring trivial and avoids prop-drilling through the shell.
 *
 * The chosen workspace is mirrored to localStorage under
 * {@link STORAGE_KEY} so it survives reloads and is roughly synced across
 * tabs via the `storage` event. We tolerate (silently) the localStorage
 * being unavailable — e.g. privacy mode — by skipping persistence; the
 * provider still works for the lifetime of the session.
 */

export const STORAGE_KEY = 'aiescape-docfiller.current-workspace-uuid'

interface WorkspaceContextValue {
  /** All workspaces the user belongs to. Empty array when the user has none yet. */
  workspaces: readonly WorkspaceRead[]
  /**
   * Active workspace. `null` while the list is still loading, when the user
   * has zero workspaces, or when the previously-selected workspace is no
   * longer available (left, deleted, …) and a new default hasn't been
   * picked yet.
   */
  currentWorkspace: WorkspaceRead | null
  /** Convenience aliases — null when no workspace is active. */
  currentWorkspaceUuid: string | null
  currentRole: WorkspaceRole | null
  isPersonal: boolean
  /** True until the workspaces list has resolved at least once. */
  isLoading: boolean
  /** Query error, surfaced so the switcher can render an error state. */
  error: Error | null
  /**
   * Switch to a different workspace. The uuid must belong to the user's
   * list; calling with an unknown uuid is a no-op (defensive — guards
   * against stale links). Pass `null` to clear the selection.
   */
  setCurrentWorkspace: (uuid: string | null) => void
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

function readStoredUuid(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    // Private mode, locked-down sandbox, …
    return null
  }
}

function writeStoredUuid(uuid: string | null): void {
  if (typeof window === 'undefined') return
  try {
    if (uuid) window.localStorage.setItem(STORAGE_KEY, uuid)
    else window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // see readStoredUuid
  }
}

/**
 * Pick a sensible default when the stored uuid is missing or stale.
 *
 *   1. The personal workspace (every user has exactly one — server seeds it
 *      on first sign-in).
 *   2. Otherwise, the first workspace in the list (server returns them
 *      sorted by `created_at`).
 *   3. `null` when the user has none yet (rare — only happens if the seed
 *      job hasn't run for an externally-provisioned account).
 */
function pickDefault(workspaces: readonly WorkspaceRead[]): WorkspaceRead | null {
  if (workspaces.length === 0) return null
  return workspaces.find((w) => w.is_personal) ?? workspaces[0] ?? null
}

interface WorkspaceProviderProps {
  children: ReactNode
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const { data, isLoading, error } = useWorkspaces()
  const workspaces = useMemo<readonly WorkspaceRead[]>(() => data ?? [], [data])

  // Pull the initial selection lazily from localStorage so SSR (jsdom in
  // tests) doesn't trip over `window`.
  const [selectedUuid, setSelectedUuid] = useState<string | null>(() =>
    readStoredUuid(),
  )

  // Cross-tab sync: when another tab writes a new uuid, follow along. This
  // is best-effort — same-tab changes don't fire `storage`, which is fine
  // because the local setter already updates state synchronously.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return
      setSelectedUuid(e.newValue)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Resolve the active workspace from the loaded list. If the stored uuid
  // is stale (workspace was deleted or the user lost access), fall back to
  // the default so the UI never gets stuck on a phantom selection.
  const currentWorkspace = useMemo<WorkspaceRead | null>(() => {
    if (workspaces.length === 0) return null
    if (selectedUuid) {
      const match = workspaces.find((w) => w.uuid === selectedUuid)
      if (match) return match
    }
    return pickDefault(workspaces)
  }, [workspaces, selectedUuid])

  // Keep localStorage in sync with the resolved selection. We persist the
  // resolved uuid (not the raw stored one) so a stale entry gets repaired
  // on first load. Persisting `null` when the user has no workspaces yet
  // is intentional — it clears any leftover entry from a previous account.
  useEffect(() => {
    writeStoredUuid(currentWorkspace?.uuid ?? null)
  }, [currentWorkspace])

  const setCurrentWorkspace = useCallback(
    (uuid: string | null) => {
      if (uuid === null) {
        setSelectedUuid(null)
        return
      }
      // Defensive: ignore unknown uuids so a bad deep-link doesn't strand
      // the user on a workspace that isn't theirs.
      if (workspaces.some((w) => w.uuid === uuid)) {
        setSelectedUuid(uuid)
      }
    },
    [workspaces],
  )

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      workspaces,
      currentWorkspace,
      currentWorkspaceUuid: currentWorkspace?.uuid ?? null,
      currentRole: currentWorkspace?.role ?? null,
      isPersonal: currentWorkspace?.is_personal ?? false,
      isLoading,
      error: error instanceof Error ? error : error ? new Error(String(error)) : null,
      setCurrentWorkspace,
    }),
    [workspaces, currentWorkspace, isLoading, error, setCurrentWorkspace],
  )

  return (
    <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
  )
}

/**
 * Hook accessor for the current workspace. Throws when called outside a
 * `<WorkspaceProvider>` — that's an error in the component tree, not
 * something to silently no-op.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) {
    throw new Error(
      'useCurrentWorkspace must be used inside a <WorkspaceProvider>. ' +
        'Make sure the component is rendered under the authenticated app shell.',
    )
  }
  return ctx
}

/**
 * Soft variant — returns `null` outside a provider instead of throwing.
 * Useful for primitives that may be rendered both inside and outside the
 * authenticated shell (e.g. error boundaries).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentWorkspaceOrNull(): WorkspaceContextValue | null {
  return useContext(WorkspaceContext)
}
